import React from 'react';
import { Button } from '@mui/material';
import { Undo as UndoIcon, Redo as RedoIcon } from '@mui/icons-material';
import { PatternCanvas } from './components/PatternCanvas/PatternCanvas';
import { StitchesPanel } from './components/StitchesPanel/StitchesPanel';
import { MenuBar } from './components/MenuBar/MenuBar';
import { NewProjectDialog } from './components/NewProjectDialog/NewProjectDialog';
import { CopyrightFooter } from './components/CopyrightFooter';
import { ConfirmDialog } from './components/ConfirmDialog';
import { Pattern, AlignmentSettings, Stitch, Point } from './types/pattern';
import { savePattern, loadPattern, exportPattern, importPattern } from './utils/storage';
import { exportImage } from './utils/exportImage';
import { useLocalization } from './localization/LocalizationContext';
import { generateSymmetricStitches } from './utils/symmetryUtils';
import { usePatternHistory } from './hooks/usePatternHistory';
import { useToolState } from './hooks/useToolState';
import { useShiftKey } from './hooks/useShiftKey';
import { ToolProvider } from './contexts/ToolContext';

const initialPattern: Pattern = {
  id: '1',
  name: 'New Pattern',
  stitches: [],
  center: { x: 400, y: 400 },
  radius: 300,
  orientationCenter: { x: 400, y: 400 },
  symmetryAxes: 0,
  alignmentSettings: {
    showGuides: true,
    radialAngleStep: 30,
    circleStep: 30,
    wedges: 12,
    circlesCount: 10,
    stitchScale: 1.5
  },
  createdAt: new Date(),
  updatedAt: new Date()
};

function App() {
  const [savedOnMount] = React.useState<Pattern | null>(() => loadPattern());
  const startPattern = savedOnMount || initialPattern;

  const {
    pattern, setPattern, patternRef, historyState,
    updatePattern, pushToHistory, debouncedSave,
    handleUndo, handleRedo, resetHistory
  } = usePatternHistory(startPattern);

  const { tool, dispatch: dispatchTool } = useToolState();
  const shiftKey = useShiftKey();
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [isDraggingOrientationCenter, setIsDraggingOrientationCenter] = React.useState(false);
  const [isNewProjectDialogOpen, setIsNewProjectDialogOpen] = React.useState(!savedOnMount);
  const [tempSettings, setTempSettings] = React.useState<AlignmentSettings>(initialPattern.alignmentSettings);
  const [confirmNewFile, setConfirmNewFile] = React.useState(false);
  const { t, language } = useLocalization();

  // --- Stitch action callbacks ---

  const handleStitchMove = React.useCallback((stitchId: string, updatedStitch: Stitch) => {
    setPattern(prev => ({
      ...prev,
      stitches: prev.stitches.map(s => s.id === stitchId ? updatedStitch : s)
    }));
  }, [setPattern]);

  const handleStitchDragEnd = React.useCallback(() => {
    const current = patternRef.current;
    pushToHistory(current);
    debouncedSave(current);
  }, [patternRef, pushToHistory, debouncedSave]);

  const handleStitchClick = React.useCallback((stitch: Stitch | null) => {
    if (!tool.stitchType) {
      dispatchTool({ type: 'SELECT_STITCH', stitch });
    }
  }, [tool.stitchType, dispatchTool]);

  const handleDeleteStitch = React.useCallback(() => {
    if (tool.selectedStitch) {
      updatePattern({
        ...patternRef.current,
        stitches: patternRef.current.stitches.filter(s => s.id !== tool.selectedStitch!.id)
      });
      dispatchTool({ type: 'CLEAR_SELECTION' });
    }
  }, [tool.selectedStitch, updatePattern, patternRef, dispatchTool]);

  const handleEraserDelete = React.useCallback((stitch: Stitch) => {
    setPattern(prev => ({
      ...prev,
      stitches: prev.stitches.filter(s => s.id !== stitch.id)
    }));
  }, [setPattern]);

  const handleRecolorStitch = React.useCallback((stitch: Stitch) => {
    setPattern(prev => ({
      ...prev,
      stitches: prev.stitches.map(s => s.id === stitch.id ? { ...s, color: tool.color } : s)
    }));
  }, [tool.color, setPattern]);

  const handleToolBrushEnd = React.useCallback(() => {
    const current = patternRef.current;
    pushToHistory(current);
    debouncedSave(current);
  }, [patternRef, pushToHistory, debouncedSave]);

  const handleAddStitch = React.useCallback((stitch: Stitch) => {
    const current = patternRef.current;
    const axes = shiftKey.ref.current ? 0 : current.symmetryAxes;
    const copies = generateSymmetricStitches(stitch, current.center, current.orientationCenter, axes);
    updatePattern({
      ...current,
      stitches: [...current.stitches, ...copies]
    });
  }, [updatePattern, patternRef, shiftKey.ref]);

  // --- Keyboard shortcuts ---

  const handleKeyDown = React.useCallback((event: KeyboardEvent) => {
    if (event.code === 'Escape') {
      dispatchTool({ type: 'CLEAR_SELECTION' });
    } else if (event.code === 'Delete' && tool.selectedStitch) {
      handleDeleteStitch();
    } else if ((event.ctrlKey || event.metaKey) && event.code === 'KeyZ') {
      if (event.shiftKey) handleRedo();
      else handleUndo();
    }
  }, [tool.selectedStitch, handleDeleteStitch, handleRedo, handleUndo, dispatchTool]);

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => { window.removeEventListener('keydown', handleKeyDown); };
  }, [handleKeyDown]);

  // --- Settings & file callbacks ---

  const handleAlignmentSettingsChange = React.useCallback((settings: AlignmentSettings) => {
    setPattern(prev => {
      const updated = { ...prev, alignmentSettings: settings };
      debouncedSave(updated);
      return updated;
    });
  }, [debouncedSave, setPattern]);

  const handleSymmetryChange = React.useCallback((axes: number) => {
    setPattern(prev => {
      const updated = { ...prev, symmetryAxes: axes };
      debouncedSave(updated);
      return updated;
    });
  }, [debouncedSave, setPattern]);

  const handleNewFile = React.useCallback(() => {
    if (pattern.stitches.length > 0) {
      setConfirmNewFile(true);
      return;
    }
    setTempSettings(initialPattern.alignmentSettings);
    setIsNewProjectDialogOpen(true);
  }, [pattern.stitches]);

  const handleConfirmNewFile = React.useCallback(() => {
    setConfirmNewFile(false);
    setTempSettings(initialPattern.alignmentSettings);
    setIsNewProjectDialogOpen(true);
  }, []);

  const handleNewProjectConfirm = React.useCallback((settings: AlignmentSettings) => {
    const newPattern: Pattern = { ...initialPattern, alignmentSettings: settings };
    resetHistory(newPattern);
    setIsNewProjectDialogOpen(false);
    savePattern(newPattern);
  }, [resetHistory]);

  const handleExportPattern = React.useCallback(() => {
    exportPattern(pattern);
  }, [pattern]);

  const handleImportPattern = React.useCallback(async () => {
    const imported = await importPattern();
    if (imported) {
      resetHistory(imported);
      savePattern(imported);
    }
  }, [resetHistory]);

  const handleSaveImage = React.useCallback(async (format: 'png' | 'jpg') => {
    if (!canvasRef.current) return;
    await exportImage({
      canvas: canvasRef.current,
      pattern,
      format,
      translations: { stitchTypes: t.stitchTypes, legend: t.legend },
      language,
    });
  }, [pattern, t, language]);

  const handleOrientationCenterMove = React.useCallback((position: Point) => {
    setPattern(prev => {
      const updated = { ...prev, orientationCenter: position };
      debouncedSave(updated);
      return updated;
    });
  }, [debouncedSave, setPattern]);

  const handleOrientationCenterDoubleClick = React.useCallback(() => {
    setPattern(prev => {
      const updated = { ...prev, orientationCenter: prev.center };
      debouncedSave(updated);
      return updated;
    });
  }, [debouncedSave, setPattern]);

  const currentSettings = isNewProjectDialogOpen ? tempSettings : pattern.alignmentSettings;

  return (
    <ToolProvider value={{ tool, dispatch: dispatchTool }}>
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MenuBar
        settings={pattern.alignmentSettings}
        onSettingsChange={handleAlignmentSettingsChange}
        onNewFile={handleNewFile}
        onSaveImage={handleSaveImage}
        onExportPattern={handleExportPattern}
        onImportPattern={handleImportPattern}
        symmetryAxes={pattern.symmetryAxes}
        onSymmetryChange={handleSymmetryChange}
        activeTool={tool.activeTool}
        onToolChange={(t) => dispatchTool({ type: 'SET_TOOL', tool: t })}
      />
      <div style={{ display: 'flex', flex: 1, minHeight: 0, flexWrap: 'wrap', overflow: 'auto' }}>
        <div style={{ flex: 1, minWidth: 816, padding: '8px' }}><div style={{ display: 'inline-flex', flexDirection: 'column' }}>
          <PatternCanvas
            ref={canvasRef}
            pattern={{ ...pattern, alignmentSettings: currentSettings }}
            onStitchMove={handleStitchMove}
            onStitchDragEnd={handleStitchDragEnd}
            onAddStitch={handleAddStitch}
            onStitchClick={handleStitchClick}
            isDraggingOrientationCenter={isDraggingOrientationCenter}
            onOrientationCenterMove={handleOrientationCenterMove}
            onOrientationCenterDoubleClick={handleOrientationCenterDoubleClick}
            setIsDraggingOrientationCenter={setIsDraggingOrientationCenter}
            onEraserDelete={handleEraserDelete}
            onRecolorStitch={handleRecolorStitch}
            onToolBrushEnd={handleToolBrushEnd}
          />
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <Button startIcon={<UndoIcon />} onClick={handleUndo} disabled={historyState.index === 0} variant="outlined" size="small">
              {t.menuBar.undo}
            </Button>
            <Button startIcon={<RedoIcon />} onClick={handleRedo} disabled={historyState.index === historyState.entries.length - 1} variant="outlined" size="small">
              {t.menuBar.redo}
            </Button>
          </div>
        </div></div>
        <StitchesPanel
          settings={pattern.alignmentSettings}
          onChange={handleAlignmentSettingsChange}
          isNewProjectDialogOpen={isNewProjectDialogOpen}
          onStitchSelect={(type: string) => dispatchTool({ type: 'SELECT_STITCH_TYPE', stitchType: type })}
          selectedStitchType={tool.stitchType}
          selectedColor={tool.color}
          onColorSelect={(color: string) => dispatchTool({ type: 'SELECT_COLOR', color })}
        />
      </div>
      <NewProjectDialog
        open={isNewProjectDialogOpen}
        onClose={() => setIsNewProjectDialogOpen(false)}
        onConfirm={handleNewProjectConfirm}
        settings={tempSettings}
        onSettingsChange={setTempSettings}
      />
      <ConfirmDialog
        open={confirmNewFile}
        title={t.menuBar.file.newFile}
        message={t.menuBar.file.unsavedChangesWarning}
        onConfirm={handleConfirmNewFile}
        onCancel={() => setConfirmNewFile(false)}
      />
      <CopyrightFooter />
    </div>
    </ToolProvider>
  );
}

export default App;
